import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { t } from "./i18n";
import { logout } from "./utils/auth";
import { useDrawer } from "./DrawerContext";

const languages = [
  {
    code: "en",
    label: "EN",
    icon: "https://cdn-icons-png.flaticon.com/512/197/197374.png",
  },
  {
    code: "es",
    label: "ES",
    icon: "https://cdn-icons-png.flaticon.com/512/197/197593.png",
  },
];

export default function Header({
  currentLang,
  onLangChange,
  minimal = false,
  portalType = "patient",
}) {
  const [open, setOpen] = useState(false);
  const { openDrawer } = useDrawer();
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const current = languages.find((l) => l.code === currentLang) || languages[0];

  if (minimal) {
    return (
      <header className="sticky top-0 bg-white shadow-sm py-2 px-8 flex items-center justify-end z-50">
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1 px-3 py-1 rounded bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-800 font-semibold text-xs focus:outline-none"
          >
            <img
              src={current.icon}
              alt={current.label}
              className="w-5 h-5 mr-1"
            />
            {current.label}
            <svg
              className="ml-1 w-3 h-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {open && (
            <div className="absolute right-0 mt-2 w-24 bg-white border border-gray-200 rounded shadow z-10">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    onLangChange(lang.code);
                    setOpen(false);
                  }}
                  className={`flex items-center w-full px-3 py-2 text-xs gap-2 hover:bg-blue-50 ${
                    currentLang === lang.code
                      ? "font-bold text-blue-800"
                      : "text-gray-700"
                  }`}
                >
                  <img src={lang.icon} alt={lang.label} className="w-4 h-4" />
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>
    );
  }

  // Determine portal title
  const portalTitle =
    portalType === "doctor"
      ? t("doctorPortal", currentLang)
      : t("patientPortal", currentLang);

  return (
    <header className="sticky top-0 bg-white shadow-sm py-4 px-8 flex items-center justify-between z-50">
      <Link
        to={
          portalType === "patient" ? "/patient-dashboard" : "/doctor-dashboard"
        }
        className="font-bold text-lg text-blue-800 cursor-pointer"
      >
        {portalTitle}
      </Link>
      <div className="flex items-center gap-6">
        <nav className="hidden md:flex gap-6">
          <a
            href=""
            className="text-blue-800 font-medium border-b-2 border-blue-800 pb-1"
          >
            {t("dashboard", currentLang)}
          </a>
          <button
            type="button"
            className="text-pink-500 font-medium"
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            {t("logout", currentLang)}
          </button>
        </nav>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1 px-3 py-1 rounded bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-800 font-semibold text-xs focus:outline-none"
          >
            <img
              src={current.icon}
              alt={current.label}
              className="w-5 h-5 mr-1"
            />
            {current.label}
            <svg
              className="ml-1 w-3 h-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {open && (
            <div className="absolute right-0 mt-2 w-24 bg-white border border-gray-200 rounded shadow z-10">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    onLangChange(lang.code);
                    setOpen(false);
                  }}
                  className={`flex items-center w-full px-3 py-2 text-xs gap-2 hover:bg-blue-50 ${
                    currentLang === lang.code
                      ? "font-bold text-blue-800"
                      : "text-gray-700"
                  }`}
                >
                  <img src={lang.icon} alt={lang.label} className="w-4 h-4" />
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          className="md:hidden"
          onClick={openDrawer}
          aria-label="Open sidebar menu"
        >
          <svg
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
    </header>
  );
}
